import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GitMerge, Undo2, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { LoadError, errMsg } from "./LoadError";
import { ukDateTime } from "./crmPipelineShared";

const Side = ({ s, testId }) => (
  <Link to={s.link} className="flex-1 min-w-0 bg-white/[0.04] rounded-lg p-3 hover:bg-white/[0.08]" data-testid={testId}>
    <div className="text-white text-sm font-medium truncate">{s.name}</div>
    <div className="text-[11px] text-emerald-100/60 truncate">{s.email || "no email"}</div>
    <div className="text-[11px] text-emerald-100/60">{s.phone || "no phone"}{s.postcode ? ` · ${s.postcode}` : ""}</div>
    <div className="text-[10px] text-emerald-100/40 mt-1">{s.type || "—"} · {s.records} record{s.records > 1 ? "s" : ""}</div>
  </Link>
);

function Pair({ p, onDone }) {
  const [busy, setBusy] = useState(false);
  const key = `${p.a.customer_id}-${p.b.customer_id}`;
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e, "Could not save")); setBusy(false); } };
  return (
    <li className="bg-[#0F2E23] border border-white/10 rounded-xl p-3.5" data-testid={`dup-pair-${key}`}>
      <div className="flex flex-wrap gap-1.5 mb-2">{p.reasons.map((r) => <span key={r} className="text-[11px] px-2 py-0.5 rounded-full bg-brass/15 text-brass">{r}</span>)}</div>
      <div className="flex gap-2"><Side s={p.a} testId={`dup-a-${key}`} /><Side s={p.b} testId={`dup-b-${key}`} /></div>
      <div className="flex gap-2 mt-3">
        <button disabled={busy} data-testid={`dup-merge-${key}`} onClick={() => act(() => api.post("/crm/customers/merge", { keep: { store: p.a.store, id: p.a.id }, merge: { store: p.b.store, id: p.b.id } }), "Merged — you can undo below")}
          className="flex-1 flex items-center justify-center gap-1.5 bg-brass/15 border border-brass/40 text-brass font-semibold rounded-lg h-10 text-sm disabled:opacity-50"><GitMerge className="w-4 h-4" />Same person — merge</button>
        <button disabled={busy} data-testid={`dup-dismiss-${key}`} onClick={() => act(() => api.post("/crm/customers/review/dismiss", { a: p.a.customer_id, b: p.b.customer_id }), "Kept separate")}
          className="flex-1 flex items-center justify-center gap-1.5 bg-white/[0.06] border border-white/15 rounded-lg h-10 text-sm text-white"><X className="w-4 h-4" />Not the same</button>
      </div>
    </li>
  );
}

export default function CrmDuplicates() {
  const [review, setReview] = useState(null);
  const [log, setLog] = useState([]);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    setErr("");
    Promise.all([api.get("/crm/customers/review"), api.get("/crm/merge-log")])
      .then(([r, l]) => { setReview(r.data); setLog(l.data.merges); }).catch((e) => setErr(errMsg(e, "Could not load duplicates")));
  }, []);
  useEffect(load, [load]);
  const runSync = async () => { try { const { data } = await api.post("/crm/customers/sync"); toast.success(`${data.merges} automatic merge${data.merges === 1 ? "" : "s"}`); load(); } catch (e) { toast.error(errMsg(e, "Check failed")); } };
  const undo = async (id) => { try { await api.post(`/crm/merge-log/${id}/undo`); toast.success("Merge undone — records kept separate"); load(); } catch (e) { toast.error(errMsg(e, "Could not undo")); } };

  if (err && !review) return <CrmShell title="Duplicates"><LoadError message={err} onRetry={load} testId="dup-error" /></CrmShell>;
  if (!review) return <CrmShell title="Duplicates"><div className="text-emerald-100/50 text-sm py-10 text-center">Loading…</div></CrmShell>;
  return (
    <CrmShell title="Duplicates">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-emerald-100/60 max-w-xl">Same email or same phone merge automatically. Possible matches below need your decision. Nothing is deleted. Notes, history, properties and referrals stay together, and every merge can be undone.</p>
        <button onClick={runSync} data-testid="dup-run-sync" className="shrink-0 flex items-center gap-1.5 text-xs bg-white/[0.06] rounded-lg px-3 py-2 text-white"><RefreshCw className="w-3.5 h-3.5" />Check now</button>
      </div>
      <h2 className="text-sm font-semibold text-white mb-2">Possible duplicates <span className="text-emerald-100/40 font-normal" data-testid="dup-total">({review.total})</span></h2>
      {review.pairs.length === 0 ? <div className="text-sm text-emerald-100/50 bg-[#0F2E23] rounded-xl border border-white/10 p-6 text-center mb-6" data-testid="dup-empty">No possible duplicates to review.</div>
        : <ul className="space-y-2.5 mb-8" data-testid="dup-list">{review.pairs.map((p) => <Pair key={`${p.a.customer_id}-${p.b.customer_id}`} p={p} onDone={load} />)}</ul>}
      <h2 className="text-sm font-semibold text-white mb-2">Merge history</h2>
      <ul className="space-y-2" data-testid="merge-log">
        {log.slice(0, 60).map((m) => (
          <li key={m.id} className={`flex items-center gap-3 bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2.5 ${m.undone ? "opacity-50" : ""}`} data-testid={`merge-${m.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{(m.names || []).join(" · ") || "Customer"}</div>
              <div className="text-[11px] text-emerald-100/50">{m.kind === "auto" ? "Automatic" : "Manual"} · {m.reason} · {m.size} records · {ukDateTime(m.created_at)}{m.undone ? " · undone" : ""}</div>
            </div>
            {!m.undone && <button onClick={() => undo(m.id)} data-testid={`merge-undo-${m.id}`} className="flex items-center gap-1 text-xs text-brass hover:underline"><Undo2 className="w-3.5 h-3.5" />Undo</button>}
          </li>
        ))}
        {log.length === 0 && <li className="text-xs text-emerald-100/40">No merges yet.</li>}
      </ul>
    </CrmShell>
  );
}
